import React, { useEffect, useState, useMemo, useRef } from 'react'
import { makeStyles } from '@mui/styles'
import debounce from 'lodash/debounce'
import clsx from 'clsx'
import { useFormikContext } from 'formik'

import lang from '@/lang'
import request from '@/modules/base/util/request'
import config from '@/config'
import InputLoader from '@/modules/base/components/inputs/loader/loader'
import TextInput from '@/modules/base/components/inputs/text-input/text-input'

const {
  emptyRequiredFieldErrorMessage: NOT_EMPTY_MESSAGE,
} = lang

const stripSpaces = (value = '') => value.replace(/\s/gm, '')
export const formatIbanGroups = (value = '') => {
  if (!value) {
    return ''
  }
  const cleanValue = stripSpaces(value)
  if (!cleanValue) {
    return ''
  }

  return cleanValue.match(/.{1,4}/g)?.join(' ') || ''
}

const useStyles = makeStyles((theme) => ({
  bankName: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 300,
    overflow: 'hidden',
    [theme.breakpoints.down('lg')]: {
      whiteSpace: 'normal',
    },
  },
  creditInstitutionLabel: {
    color: theme.darkerGrey,
  },
  creditInstitutionWrapper: {
    fontSize: theme.fontSizes.smaller,
    paddingTop: 10,
    display: 'flex',
    gap: 5,
  },
  container: {
    position: 'relative',
  },
  loaderWrapper: {
    position: 'absolute',
    top: '45px',
    right: '10px',
  },
  loader: {
    background: 'transparent',
  },
}))

interface IbanInputProps {
    errorClass: string,
    errorMessage: string,
    name: string,
    label: string,
    placeholder: string,
    value: string,
    required: boolean,
    showCreditInstitution: boolean,
    useFormik: boolean,
    disabled: boolean,
    readOnly: boolean,
    maxLength: number,
    inputStyle: string,
    loaderClass: string,
    customClass: string,
    classNameCreditInstitution?: string,
    fillAnimationOptions?: { show: boolean, color: string },
    inputRef: (ref: HTMLInputElement) => void,
    onInputChanged: (value: string, name: string) => void,
    onBlur: (value: string, name: string) => void,
    onChange: (value: string, name: string) => void,
    customClassLabel?: string,
}

interface IbanInputRequiredProps {
  dataQsId: string,
  handleValidation: (ibanIsValid: boolean, bankData?: { bic?: string, iban?: string, bankname?: string}) => void,
}

const IbanInput = (props: Partial<IbanInputProps> & IbanInputRequiredProps) => {
  const {
    name = '',
    value = '',
    useFormik = true,
    showCreditInstitution,
    maxLength,
    loaderClass,
    customClass,
    dataQsId = 'iban-input',
    classNameCreditInstitution,
    fillAnimationOptions,
    handleValidation,
    onInputChanged,
    customClassLabel,
  } = props
  const classes = useStyles(props)
  const formikContext = useFormik ? useFormikContext() : null
  const [ibanErrorMessage, setIbanErrorMessage] = useState(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [bankName, setBankName] = useState<string | null>(null)

  const validationRef = useRef<((ibanIsValid: boolean, bankData?: { bic?: string, iban?: string, bankname?: string}) => void) | null>(null)
  validationRef.current = handleValidation

  const onValidation = useMemo(() => {
    return debounce((value) => {
      validateIban(value)
    }, 500)
  }, [])

  const cleanValue = stripSpaces(formikContext?.values?.[name] ?? value ?? '')
  const formattedValue = formatIbanGroups(cleanValue)
  const formattedMaxLength = maxLength
    ? maxLength + Math.floor((maxLength - 1) / 4)
    : maxLength

  useEffect(() => {
    const newValue = stripSpaces(formikContext?.values?.[name] ?? value ?? '')
    if (newValue !== '') {
      onValidation(newValue)
    }
  }, [value, formikContext?.values[name]])

  const validateIban = (bankAccountIban) => {
    const { errorMessage: ibanErrorMessage = lang.ibanErrorMessage } = props
    const handleValidation = validationRef.current

    const cleanBankAccountIban = stripSpaces(bankAccountIban || '')

    if (!cleanBankAccountIban || cleanBankAccountIban.length < 15) {
      handleValidation?.(false)
      setBankName('')
      setIbanErrorMessage(ibanErrorMessage || (cleanBankAccountIban ? lang.ibanErrorMessage : NOT_EMPTY_MESSAGE))
      return
    }

    const onIbanCheckResponse = (response) => {
      setIbanErrorMessage(null)
      const {
        body: {
          data: { isIbanValid, bankname, bic },
        },
      } = response
      if (isIbanValid) {
        handleValidation?.(isIbanValid, { bankname, bic, iban: cleanBankAccountIban })
        setBankName(bankname || '')
        setIsLoading(false)
        return
      }

      if (formikContext) {
        formikContext.setFieldError(name, ibanErrorMessage || lang.ibanErrorMessage)
      }

      setBankName('')
      setIbanErrorMessage(ibanErrorMessage || lang.ibanErrorMessage)
      setIsLoading(false)
      handleValidation?.(false)
    }

    setIsLoading(true)
    const url = `${config.resourceHost}banking/check/iban/${cleanBankAccountIban.toUpperCase()}`
    return request(url, { headers: {}, isConcurrent: true })
      .then(onIbanCheckResponse)
      .catch(() => {
        setIsLoading(false)
        handleValidation?.(false)
      })
  }

  const handleBlur = (value) => {
    onValidation(stripSpaces(value))
  }

  const onChange = (value: string, name: string) => {
    const cleanValue = stripSpaces(value || '')

    // Aktualisiere Formik-Wert wenn Formik-Kontext vorhanden ist
    if (formikContext) {
      formikContext.setFieldValue(name, cleanValue)
    }

    onInputChanged?.(cleanValue, name)
    onValidation(cleanValue)
  }

  const textInputProps = {
    errorClass: '',
    inputStyle: '',
    placeholder: '',
    required: true,
    disabled: false,
    ...props,
    name,
    errorMessage: ibanErrorMessage,
    onChange,
    value: formattedValue,
    useFormik: false,
    maxLength: formattedMaxLength,
    fillAnimationOptions,
  }

  const loader = isLoading
    ? (
      <div className={clsx(classes.loaderWrapper, loaderClass)}>
        <InputLoader customClass={classes.loader} dataQsId={`${dataQsId}-loader`} />
      </div>
    )
    : null

  return (
    <div className={`${classes.container} ${customClass || ''}`} data-qs-id={dataQsId}>
      <TextInput {...textInputProps} enableAiFeatures={false} onBlur={handleBlur} customClassLabel={customClassLabel} />
      {showCreditInstitution && <div className={clsx(classes.creditInstitutionWrapper, classNameCreditInstitution)}>
        <div className={classes.creditInstitutionLabel}>{`${lang.billingDataForm.creditInstitution}: `}</div> <div className={classes.bankName} title={bankName || ''}>{bankName || lang.billingDataForm.creditInstitutionUnknown}</div>
      </div>}
      {loader}
    </div>
  )
}

export default IbanInput
